import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Campaign,
  Business,
  People,
  Refresh,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { Campaign as CampaignType, Department, User } from '../types';

interface CampaignFormData {
  name: string;
  description: string;
  departmentId: string;
  isActive: boolean;
}

const CampaignsManagementPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignType[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignType | null>(null);
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    description: '',
    departmentId: '',
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [campaignEmployees, setCampaignEmployees] = useState<{[campaignId: string]: User[]}>({});
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState<
    'name' | 'description' | 'department' | 'employees' | 'status' | 'created'
  >('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const loadCampaignEmployees = useCallback(async (campaigns: CampaignType[], date?: string) => {
    try {
      const targetDate = date || selectedDate;
      const employeeData: {[campaignId: string]: User[]} = {};
      
      // Fetch employee data for each campaign for the selected date
      for (const campaign of campaigns) {
        try {
          const response = await apiClient.schedules.getCampaignEmployees(campaign.id, targetDate);
          const scheduledEmployees = response.data.data.scheduledEmployees || [];
          employeeData[campaign.id] = scheduledEmployees.map((schedule: any) => schedule.user);
        } catch (error) {
          console.error(`Error loading employees for campaign ${campaign.id}:`, error);
          employeeData[campaign.id] = [];
        }
      }
      
      setCampaignEmployees(employeeData);
    } catch (error) {
      console.error('Error loading campaign employees:', error);
    }
  }, [selectedDate]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [campaignsData, departmentsData] = await Promise.all([
        apiClient.campaigns.getAll({ activeOnly: false }),
        apiClient.departments.getAll(),
      ]);
      setCampaigns(campaignsData.data.data);
      setDepartments(departmentsData.data.data);
      
      // Load employee-campaign associations for today
      await loadCampaignEmployees(campaignsData.data.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [loadCampaignEmployees]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenDialog = (campaign?: CampaignType) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name,
        description: campaign.description || '',
        departmentId: campaign.departmentId,
        isActive: campaign.isActive,
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        name: '',
        description: '',
        departmentId: '',
        isActive: true,
      });
    }
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCampaign(null);
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingCampaign) {
        await apiClient.campaigns.update(editingCampaign.id, formData);
        setSuccess('Campaign updated successfully');
      } else {
        await apiClient.campaigns.create(formData);
        setSuccess('Campaign created successfully');
      }

      // Close dialog immediately and reload data in background
      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (window.confirm('Are you sure you want to delete this campaign? This will affect all schedules and time entries for this campaign.')) {
      try {
        await apiClient.campaigns.delete(campaignId);
        setSuccess('Campaign deleted successfully');
        await loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete campaign');
      }
    }
  };

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find(d => d.id === departmentId);
    return department?.name || 'Unknown';
  };

  const getUsersInCampaign = (campaignId: string) => {
    return campaignEmployees[campaignId] || [];
  };

  const getSortValue = (campaign: CampaignType, key: typeof sortBy) => {
    switch (key) {
      case 'name':
        return campaign.name || '';
      case 'description':
        return campaign.description || '';
      case 'department':
        return getDepartmentName(campaign.departmentId);
      case 'employees':
        return getUsersInCampaign(campaign.id).length.toString();
      case 'status':
        return campaign.isActive ? 'active' : 'inactive';
      case 'created':
        return campaign.createdAt || '';
      default:
        return '';
    }
  };

  const handleSortChange = (key: typeof sortBy) => {
    if (sortBy === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDirection('asc');
  };

  const sortedCampaigns = [...campaigns]
    .map((campaign, index) => ({ campaign, index }))
    .sort((a, b) => {
      const aValue = getSortValue(a.campaign, sortBy);
      const bValue = getSortValue(b.campaign, sortBy);
      const comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base', numeric: true });
      if (comparison !== 0) {
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return a.index - b.index;
    })
    .map(({ campaign }) => campaign);

  const handleDateChange = async (newDate: string) => {
    setSelectedDate(newDate);
    await loadCampaignEmployees(campaigns, newDate);
  };

  const handleRefreshEmployees = async () => {
    await loadCampaignEmployees(campaigns);
  };


  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Campaign sx={{ mr: 1, verticalAlign: 'middle' }} />
          Campaigns Management
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            label="View Employee Assignments for Date"
            type="date"
            value={selectedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Tooltip title="Refresh employee assignments">
            <IconButton onClick={handleRefreshEmployees} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Campaign
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}


      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'description' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'description'}
                      direction={sortBy === 'description' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('description')}
                    >
                      Description
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'department' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'department'}
                      direction={sortBy === 'department' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('department')}
                    >
                      Department
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'employees' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'employees'}
                      direction={sortBy === 'employees' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('employees')}
                    >
                      <Box display="flex" alignItems="center">
                        <People sx={{ mr: 1, fontSize: 16 }} />
                        Employees ({new Date(selectedDate).toLocaleDateString()})
                      </Box>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'status' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'created' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'created'}
                      direction={sortBy === 'created' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('created')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCampaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Campaign sx={{ mr: 1, fontSize: 16 }} />
                        {campaign.name}
                      </Box>
                    </TableCell>
                    <TableCell>{campaign.description || 'No description'}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Business sx={{ mr: 1, fontSize: 16 }} />
                        {getDepartmentName(campaign.departmentId)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                        <People sx={{ mr: 1, fontSize: 16 }} />
                        {getUsersInCampaign(campaign.id).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No employees assigned
                          </Typography>
                        ) : (
                          getUsersInCampaign(campaign.id).map((user) => (
                            <Chip
                              key={user.id}
                              label={`${user.firstName} ${user.lastName}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 24 }}
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.isActive ? 'Active' : 'Inactive'}
                        color={campaign.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(campaign.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(campaign)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(campaign.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Campaign Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCampaign ? 'Edit Campaign' : 'Add New Campaign'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Campaign Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  label="Department"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Saving...' : (editingCampaign ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default CampaignsManagementPage;
